import * as Sentry from "@sentry/react";
import {
    Atom,
    atom,
    Getter,
    PrimitiveAtom,
    SetStateAction,
    Setter,
    WritableAtom,
} from "jotai";
import * as Yup from "yup";
import { ISchema } from "yup";

import { t } from "../i18n";
import { assertNever } from "../obj";

import {
    FieldState,
    FormAtom,
    FormValidationError,
    Option,
    ValidationActionAtom,
    ValidationResult,
} from "./formAtom/types";

/**
 * Actions at the array level, also exposes reset for convenience in a remove or reset scenario.
 */
export type SplitFormAtomAction<Item> =
    | {
          type: "remove";
          atom: FormAtom<Item>;
      }
    | {
          type: "insert";
          value: Item;
          before?: FormAtom<Item>;
      }
    | {
          type: "move";
          atom: FormAtom<Item>;
          before?: FormAtom<Item>;
      }
    | {
          type: "reset";
          atom: FormAtom<Item>;
      };

type SplitFormAtomInner<Item> = WritableAtom<
    FormAtom<Item>[],
    [action: SplitFormAtomAction<Item>],
    Promise<void>
>;
export type SplitFormAtom<Item> = SplitFormAtomInner<Item> & {
    /**
     * The item validation schema
     */
    schema: ISchema<Item>;
};

export type SplitSchemaBuilder<Item> =
    | Yup.ISchema<Item>
    | ((formAtomsAtom: SplitFormAtomInner<Item>) => Yup.ISchema<Item>);

// Taken and adapted from jotai source: https://github.com/pmndrs/jotai/blob/main/src/vanilla/utils/splitAtom.ts
const getCached = <T>(c: () => T, m: WeakMap<WeakKey, T>, k: object): T =>
    (m.has(k) ? m : m.set(k, c())).get(k) as T;
const cache1 = new WeakMap();
const memo2 = <T>(create: () => T, dep1: object, dep2: object): T => {
    const cache2: WeakMap<object, T> = getCached(
        () => new WeakMap(),
        cache1,
        dep1
    );
    return getCached(create, cache2, dep2);
};
const cacheKeyForEmptyKeyExtractor = {};
const isFunction = <T>(x: T): x is T & ((...args: any[]) => any) =>
    typeof x === "function";

/**
 * Transform a form atom holding an array into an atom holding an array of form atoms,
 * one for each array entry.
 * @param arrAtom the atom to split
 * @param keyExtractor item's key, useful for performant and well functioning array reordering
 * The item atoms schema is derived from the array schema.
 */
export function splitFormAtom<Item, Key>(
    arrAtom: FormAtom<Item[]>,
    keyExtractor?: (item: Item) => Key
): SplitFormAtom<Item> {
    return memo2(
        () => {
            type ItemAtom = FormAtom<Item>;

            interface Mapping {
                arr: Item[];
                atomList: ItemAtom[];
                keyList: Key[];
            }

            function splittedFormAtom(
                key: Key,
                oldArr: Item[]
            ): FormAtom<Item> {
                const arrAndIndex = (get: Getter) => {
                    const prev = get(mappingAtom);
                    const arr = get(arrAtom);
                    const mapping = getMapping(arr, prev?.arr);
                    const index = mapping.keyList.indexOf(key);
                    return [arr, index] as const;
                };
                let currentChangeId = 0;
                const derivedAtom: WritableAtom<
                    Item,
                    [SetStateAction<Item>],
                    Promise<void>
                > = atom(
                    (get: Getter) => {
                        const [arr, index] = arrAndIndex(get);
                        if (index < 0 || index >= arr.length) {
                            // returning a stale value to avoid errors for use cases such as react-spring
                            const prevItem =
                                oldArr[getMapping(oldArr).keyList.indexOf(key)];
                            if (prevItem) {
                                return prevItem;
                            }
                            throw new Error(
                                "splitAtom: index out of bounds for read"
                            );
                        }
                        return arr[index];
                    },
                    async (
                        get: Getter,
                        set: Setter,
                        update: SetStateAction<Item>
                    ) => {
                        const startChangeId = (currentChangeId += 1);
                        const [arr, index] = arrAndIndex(get);
                        if (index < 0 || index >= arr.length) {
                            throw new Error(
                                "splitAtom: index out of bounds for write"
                            );
                        }
                        const nextItem = isFunction(update)
                            ? update(arr[index])
                            : update;

                        // Persist initial value for reset
                        const initialValue = get(initialValueAtom);
                        if (!initialValue.defined) {
                            set(initialValueAtom, {
                                defined: true,
                                value: arr[index] as Awaited<Item>,
                            }); // TODO: Check if awaited cast here is correct
                        }

                        if (!Object.is(arr[index], nextItem)) {
                            // Validate the new value
                            try {
                                const validatedValue = await schema.validate(
                                    nextItem,
                                    { context: { get, set } }
                                );
                                if (startChangeId !== currentChangeId) {
                                    console.debug(
                                        `Superseded by another change (${currentChangeId} != ${startChangeId}) after validation`,
                                        validatedValue
                                    );
                                    return;
                                }

                                await set(arrAtom, [
                                    ...arr.slice(0, index),
                                    validatedValue,
                                    ...arr.slice(index + 1),
                                ]);
                            } catch (err: unknown) {
                                const yupError = err as Yup.ValidationError;
                                if (yupError.errors === undefined) {
                                    const sentryError = new Error(
                                        "Field validation failed but got no error message. Fallback to generic error!"
                                    );
                                    console.error(sentryError);
                                    Sentry.captureException(sentryError);
                                }
                                console.error(yupError.errors ?? err);
                                if (currentChangeId !== startChangeId) {
                                    console.debug(
                                        `Superseded by another change (${currentChangeId} != ${startChangeId}) in error catch`,
                                        nextItem
                                    );
                                    return;
                                }
                                const error = {
                                    yup: yupError,
                                    value:
                                        yupError.errors?.join(", ") ??
                                        t("Validation_Invalid"),
                                };
                                set(fieldStateAtom, {
                                    type: "ERRORED",
                                    dirty: false, // fieldStateAtom preserves parent dirty state if needed.
                                    value: nextItem,
                                    error,
                                    validationSucceeded: false,
                                });
                            }
                        }
                    }
                );
                const getChildFieldState = (
                    parentFieldState: FieldState<Item[]>,
                    index: number
                ): FieldState<Item> => {
                    switch (parentFieldState.type) {
                        case "NOT_TOUCHED":
                            return {
                                type: "NOT_TOUCHED",
                                dirty: parentFieldState.dirty,
                            };
                        case "CACHED":
                            return {
                                type: "CACHED",
                                dirty: parentFieldState.dirty,
                                value: parentFieldState.value[
                                    index
                                ] as Awaited<Item>,
                            };
                        case "VALIDATING":
                            return {
                                type: "VALIDATING",
                                dirty: parentFieldState.dirty,
                                value: parentFieldState.value[
                                    index
                                ] as Awaited<Item>,
                                validationPromise:
                                    parentFieldState.validationPromise,
                            };
                        case "DISPATCHING":
                            return {
                                type: "DISPATCHING",
                                dirty: parentFieldState.dirty,
                                value: parentFieldState.value[
                                    index
                                ] as Awaited<Item>,
                                dispatchPromise:
                                    parentFieldState.dispatchPromise,
                            };
                        case "DISPATCHED":
                            return {
                                type: "DISPATCHED",
                                dirty: parentFieldState.dirty,
                            };
                        case "ERRORED":
                            const error = extractSplitAtomFormValidationError(
                                parentFieldState.error
                            );
                            const indices = error?.indices ?? [];
                            if (indices.includes(index)) {
                                return {
                                    type: "ERRORED",
                                    dirty: parentFieldState.dirty,
                                    error: {
                                        ...error!,
                                        value:
                                            error!.indicesToMessage?.[index] ??
                                            error!.value,
                                    },
                                    validationSucceeded:
                                        parentFieldState.validationSucceeded,
                                    value: parentFieldState.value[
                                        index
                                    ] as Awaited<Item>,
                                };
                            } else {
                                return {
                                    type: "CACHED",
                                    dirty: parentFieldState.dirty,
                                    value: parentFieldState.value[
                                        index
                                    ] as Awaited<Item>,
                                };
                            }
                        default:
                            assertNever(parentFieldState);
                    }
                };
                const fieldStateAtom: PrimitiveAtom<FieldState<Item>> = atom(
                    (get: Getter): FieldState<Item> => {
                        const [, index] = arrAndIndex(get);
                        const parentFieldState = get(arrAtom.fieldStateAtom);
                        return getChildFieldState(parentFieldState, index);
                    },
                    (
                        get: Getter,
                        set: Setter,
                        update: SetStateAction<FieldState<Item>>
                    ) => {
                        const [, index] = arrAndIndex(get);
                        set(
                            arrAtom.fieldStateAtom,
                            (prev: FieldState<Item[]>): FieldState<Item[]> => {
                                const fieldState = getChildFieldState(
                                    prev,
                                    index
                                );
                                const next: FieldState<Item> = isFunction(
                                    update
                                )
                                    ? update(fieldState)
                                    : update;
                                if ("value" in next) {
                                    if ("value" in prev) {
                                        return {
                                            ...prev,
                                            value: [
                                                ...prev.value.slice(0, index),
                                                next.value,
                                                ...prev.value.slice(index + 1),
                                            ],
                                        };
                                    } else {
                                        const value = get(arrAtom);
                                        switch (next.type) {
                                            case "CACHED":
                                                return {
                                                    type: "CACHED",
                                                    dirty:
                                                        prev.dirty ||
                                                        next.dirty,
                                                    value: [
                                                        ...value.slice(
                                                            0,
                                                            index
                                                        ),
                                                        next.value,
                                                        ...value.slice(
                                                            index + 1
                                                        ),
                                                    ],
                                                };
                                            case "ERRORED":
                                                return {
                                                    type: "ERRORED",
                                                    dirty:
                                                        prev.dirty ||
                                                        next.dirty,
                                                    value: [
                                                        ...value.slice(
                                                            0,
                                                            index
                                                        ),
                                                        next.value,
                                                        ...value.slice(
                                                            index + 1
                                                        ),
                                                    ],
                                                    error: {
                                                        yup: toSplitAtomValidationError(
                                                            next.error.yup,
                                                            [index]
                                                        ),
                                                        value: next.error.value,
                                                    },
                                                    validationSucceeded:
                                                        next.validationSucceeded,
                                                };
                                            case "VALIDATING":
                                            case "DISPATCHING":
                                                throw new Error(
                                                    "splitAtom: cannot set a validating or dispatching state from outside the atom"
                                                );
                                            default:
                                                assertNever(next);
                                        }
                                    }
                                }
                                return prev;
                            }
                        );
                    }
                );
                const initialValueAtom = atom({ defined: false } as Option<
                    Awaited<Item>
                >);
                const validateFieldAtom: ValidationActionAtom = atom(
                    null,
                    async (
                        get,
                        set,
                        revalidate: boolean
                    ): Promise<ValidationResult<undefined>> => {
                        // Triggers a validation of the array field and extracts the errors for the current item
                        const arrResult = await set(
                            arrAtom.validateFieldAtom,
                            revalidate
                        );
                        if (arrResult.type !== "ERROR") {
                            return arrResult;
                        }
                        const [, index] = arrAndIndex(get);
                        const itemErrors = arrResult.errors
                            .map(extractSplitAtomFormValidationError)
                            .filter(
                                (splitAtomFormValidationError) =>
                                    splitAtomFormValidationError !==
                                        undefined &&
                                    splitAtomFormValidationError.indices.includes(
                                        index
                                    )
                            ) as SplitAtomFormValidationError[];

                        if (itemErrors.length === 0) {
                            return {
                                type: "SUCCESS",
                                values: undefined,
                            };
                        }

                        return {
                            type: "ERROR",
                            errors: itemErrors,
                        };
                    }
                );
                const subscribeToForwardValidation =
                    arrAtom.subscribeToForwardValidation;
                const resetFieldAtom = atom(null, async (get, set) => {
                    const initialValue = get(initialValueAtom);

                    if (initialValue.defined) {
                        // Temporary store the initial value, invalidating current changes if needed
                        set(fieldStateAtom, {
                            type: "CACHED",
                            dirty: false,
                            value: initialValue.value,
                        });
                        await set(arrAtom, (oldArr: Item[]) => {
                            const [, index] = arrAndIndex(get);
                            return [
                                ...oldArr.slice(0, index),
                                initialValue.value,
                                ...oldArr.slice(index + 1),
                            ];
                        });
                    } else {
                        set(fieldStateAtom, {
                            type: "NOT_TOUCHED",
                            dirty: false,
                        });
                    }
                });
                const areEqual = (a: Item, b: Item) => a === b;

                return {
                    ...derivedAtom,
                    /**
                     * Internal state of the field, caches the value during the lifecycle of the field
                     */
                    fieldStateAtom,
                    /**
                     * Atom storing the old value during the first write
                     * Used to set internal state back to this value on call
                     *
                     * Can be read but not write from outside formAtom function
                     */
                    initialValueAtom,
                    /**
                     * Write only atom forcing a validation
                     * Does **not** revalidate previously validated values unless `revalidate` is set to true
                     */
                    validateFieldAtom,
                    /**
                     * Register an action atom to get forwarded validation requests
                     * Call the returned function to unsubscribe
                     */
                    subscribeToForwardValidation,
                    /**
                     * Write only atom resetting the field, dispatching (without validation) the initialValue to the parent, if any
                     */
                    resetFieldAtom,
                    /**
                     * Yup validation schema
                     */
                    schema,
                    /**
                     * Comparison function used to know if the field is dirty
                     * Defaults to object comparison
                     *
                     * You may want to define it when `Value` is an array or an object
                     */
                    areEqual,
                };
            }

            const mappingCache = new WeakMap<Item[], Mapping>();
            const getMapping = (arr: Item[], prev?: Item[]) => {
                let mapping = mappingCache.get(arr);
                if (mapping) {
                    return mapping;
                }
                const prevMapping = prev && mappingCache.get(prev);
                const atomList: ItemAtom[] = [];
                const keyList: Key[] = [];
                arr.forEach((item, index) => {
                    const key = keyExtractor
                        ? keyExtractor(item)
                        : (index as unknown as Key);
                    keyList[index] = key;
                    const cachedAtom =
                        prevMapping &&
                        prevMapping.atomList[prevMapping.keyList.indexOf(key)];
                    if (cachedAtom) {
                        atomList[index] = cachedAtom;
                        return;
                    }
                    atomList[index] = splittedFormAtom(key, arr);
                });
                if (
                    prevMapping &&
                    prevMapping.keyList.length === keyList.length &&
                    prevMapping.keyList.every((x, i) => x === keyList[i])
                ) {
                    // not changed
                    mapping = prevMapping;
                } else {
                    mapping = { arr, atomList, keyList };
                }
                mappingCache.set(arr, mapping);
                return mapping;
            };
            const mappingAtom: Atom<Mapping> & {
                init?: undefined;
            } = atom((get) => {
                const prev = get(mappingAtom);
                const arr = get(arrAtom);
                return getMapping(arr, prev?.arr);
            });

            // HACK to read mapping atom before initialization
            mappingAtom.init = undefined;
            const splittedAtom = atom(
                (get) => get(mappingAtom).atomList,
                async (get, set, action: SplitFormAtomAction<Item>) => {
                    switch (action.type) {
                        case "remove": {
                            const index = get(splittedAtom).indexOf(
                                action.atom
                            );
                            if (index >= 0) {
                                const arr = get(arrAtom);
                                await set(arrAtom, [
                                    ...arr.slice(0, index),
                                    ...arr.slice(index + 1),
                                ]);
                            }
                            break;
                        }
                        case "insert": {
                            const index = action.before
                                ? get(splittedAtom).indexOf(action.before)
                                : get(splittedAtom).length;
                            if (index >= 0) {
                                const arr = get(arrAtom);
                                await set(arrAtom, [
                                    ...arr.slice(0, index),
                                    action.value,
                                    ...arr.slice(index),
                                ]);
                            }
                            break;
                        }
                        case "move": {
                            const index1 = get(splittedAtom).indexOf(
                                action.atom
                            );
                            const index2 = action.before
                                ? get(splittedAtom).indexOf(action.before)
                                : get(splittedAtom).length;
                            if (index1 >= 0 && index2 >= 0) {
                                const arr = get(arrAtom);
                                if (index1 < index2) {
                                    await set(arrAtom, [
                                        ...arr.slice(0, index1),
                                        ...arr.slice(index1 + 1, index2),
                                        arr[index1],
                                        ...arr.slice(index2),
                                    ]);
                                } else {
                                    await set(arrAtom, [
                                        ...arr.slice(0, index2),
                                        arr[index1],
                                        ...arr.slice(index2, index1),
                                        ...arr.slice(index1 + 1),
                                    ]);
                                }
                            }
                            break;
                        }
                        case "reset": {
                            await set(action.atom.resetFieldAtom);
                            break;
                        }
                        default:
                            assertNever(action);
                    }
                }
            );
            const schema: ISchema<Item> = Yup.reach(
                arrAtom.schema,
                "[0]"
            ) as ISchema<Item>;
            return {
                ...splittedAtom,
                schema,
            };
        },
        arrAtom,
        keyExtractor || cacheKeyForEmptyKeyExtractor
    );
}

export const SPLIT_ATOM_FORM_VALIDATION_ERROR = "SplitAtomFormValidationError";

interface SplitAtomFormValidationError extends FormValidationError {
    indices: number[];
    indicesToMessage?: Record<number, string>;
}

function extractSplitAtomFormValidationError(
    error: FormValidationError
): SplitAtomFormValidationError | undefined {
    if (error.yup.params?.type !== SPLIT_ATOM_FORM_VALIDATION_ERROR) {
        return undefined;
    }
    const indices = error.yup.params.indices as number[];
    const indicesToMessage = error.yup.params.indicesToMessage as
        | Record<number, string>
        | undefined;
    return {
        yup: {
            ...error.yup,
            params: undefined,
        },
        value: error.value,
        indices,
        indicesToMessage,
    };
}

export function excludeSplitAtomFormValidationError(
    error: FormValidationError | undefined
): FormValidationError | undefined {
    if (error?.yup.params?.type !== SPLIT_ATOM_FORM_VALIDATION_ERROR) {
        return error;
    }
    return undefined;
}

export function toSplitAtomValidationError(
    error: Yup.ValidationError,
    indicesOrIndicesToMessage: number[] | Record<number, string>
): Yup.ValidationError {
    const indices = Array.isArray(indicesOrIndicesToMessage)
        ? indicesOrIndicesToMessage
        : Object.keys(indicesOrIndicesToMessage).map(Number);
    const indicesToMessage = Array.isArray(indicesOrIndicesToMessage)
        ? undefined
        : indicesOrIndicesToMessage;

    return {
        ...error,
        params: {
            ...error.params,
            type: SPLIT_ATOM_FORM_VALIDATION_ERROR,
            indices,
            indicesToMessage,
        },
    };
}
